import { HttpClient } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Authenticator, LoginFacade, NotificationFacade } from '@ridango/components';
import { BaseComponent } from '@ridango/core';
import Env from '@ridango/core/env';
import { authenticate } from '@web-eid/web-eid-library/web-eid';
import { throwError, timer, from, Observable } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, timeout } from 'rxjs/operators';

@Component({
    templateUrl: './account-personalize-page.component.html',
    styleUrls: ['./account-personalize-page.component.scss']
})
export class AccountPersonalizePageComponent extends BaseComponent {
    method: string = this.env.AUTH_FEATURES[0] || '';
    personalCode = '';
    phoneNumber: number = null;
    loading = false;

    constructor(
        @Inject('ENV') public env: Env,
        private notification: NotificationFacade,
        private http: HttpClient,
        private translate: TranslateService,
        private auth: Authenticator,
        private loginFacade: LoginFacade
    ) {
        super();
    }

    setMethod(method: string) {
        this.notification.removeAll();
        this.method = method;
    }

    loginIdCard() {
        this.loading = true;
        this.notification.removeAll();
        this.http
            .get<{ nonce: string }>(`${this.env.API_GATEWAY_URL}/identity/v1/challenge`)
            .pipe(
                switchMap((r) =>
                    from(authenticate(r.nonce, { lang: this.translate.currentLang })).pipe(
                        switchMap(
                            (token) =>
                                this.auth.submitIdCardAuth(token, r.nonce) as unknown as Observable<{ token: string }>
                        )
                    )
                ),
                catchError(() => throwError('LOGIN_ERR')),
                map((r) => r.token),
                switchMap((token) => {
                    const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/customer/personalize`;
                    return this.http.post(url, { strongAccountToken: token }).pipe(timeout(30_000));
                }),
                catchError((err) => {
                    return throwError(err.error);
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(
                async () => {
                    this.loading = false;
                    this.loginFacade.logout(true);
                    this.notification.success(this.translate.instant('LINK_PERSONAL_CODE_SUCCESS'));
                },
                (err) => {
                    this.loading = false;
                    this.notification.error(this.translate.instant(err));
                }
            );
    }

    loginMobileId() {
        this.loading = true;
        this.notification.removeAll();
        this.http
            .post<{ verificationCode: string; hash: string }>(`${this.env.API_GATEWAY_URL}/identity/v1/mid/auth/init`, {
                phoneNumber: this.env?.DEFAULT_PHONE_COUNTRY_CODE + this.phoneNumber,
                nationalIdentityNumber: this.personalCode,
                language: this.translate.currentLang,
                regionId: this.env.REGION_ID,
                clientId: this.env.OAUTH_CLIENT_ID,
                channel: this.env.CHANNEL
            })
            .pipe(
                switchMap((response) => {
                    return timer(0, 3000).pipe(
                        switchMap(() => {
                            return this.http.post<{ state: string; token: string }>(
                                `${this.env.API_GATEWAY_URL}/identity/v1/mid/challenge/poll`,
                                { hash: response.hash }
                            );
                        }),
                        filter((r) => r?.state === 'COMPLETE'),
                        take(1),
                        timeout(30_000)
                    );
                }),
                catchError(() => {
                    return throwError('LOGIN_ERR');
                }),
                map((r) => r.token),
                switchMap((token) => {
                    const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/customer/personalize`;
                    return this.http.post(url, { strongAccountToken: token }).pipe(timeout(30_000));
                }),
                catchError((err) => {
                    return throwError(err.error);
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(
                async () => {
                    this.loading = false;
                    this.loginFacade.logout(true);
                    this.notification.success(this.translate.instant('LINK_PERSONAL_CODE_SUCCESS'));
                },
                (err) => {
                    this.loading = false;
                    this.notification.error(this.translate.instant(err));
                }
            );
    }

    loginSmartId() {
        this.loading = true;
        this.notification.removeAll();
        this.http
            .post<{ verificationCode: string; hash: string }>(
                `${this.env.API_GATEWAY_URL}/identity/v1/smartid/auth/init`,
                {
                    nationalIdentityNumber: this.personalCode,
                    language: this.translate.currentLang,
                    regionId: this.env.REGION_ID,
                    clientId: this.env.OAUTH_CLIENT_ID,
                    channel: this.env.CHANNEL
                }
            )
            .pipe(
                switchMap((response) => {
                    return timer(0, 3000).pipe(
                        switchMap(() => {
                            return this.http.post<{ state: string; token: string }>(
                                `${this.env.API_GATEWAY_URL}/identity/v1/smartid/challenge/poll`,
                                { hash: response.hash }
                            );
                        }),
                        filter((r) => r?.state === 'COMPLETE'),
                        take(1),
                        timeout(30_000)
                    );
                }),
                catchError(() => {
                    return throwError('LOGIN_ERR');
                }),
                map((r) => r.token),
                switchMap((token) => {
                    const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/customer/personalize`;
                    return this.http.post(url, { strongAccountToken: token }).pipe(timeout(30_000));
                }),
                catchError((err) => {
                    return throwError(err.error);
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(
                async () => {
                    this.loading = false;
                    this.loginFacade.logout(true);
                    this.notification.success(this.translate.instant('LINK_PERSONAL_CODE_SUCCESS'));
                },
                (err) => {
                    this.loading = false;
                    this.notification.error(this.translate.instant(err));
                }
            );
    }
}
